// LocalStorage persistence for undo tree

import type { UndoTree, UndoNode } from "./tree.ts";

/**
 * Serialized format for localStorage.
 */
interface SerializedTree {
  version: number;
  nodes: SerializedNode[];
  rootId: string;
  currentId: string;
}

/**
 * Serialized node for JSON storage.
 * Same structure as UndoNode but ensures JSON compatibility.
 */
interface SerializedNode {
  id: string;
  parentId: string | null;
  childIds: string[];
  timestamp: number;
  operationType: string;
  operationLabel: string;
  delta: unknown; // OperationDelta is already JSON-compatible
  activeChildIndex: number;
}

const STORAGE_VERSION = 1;

/**
 * Save the undo tree to localStorage.
 */
export function saveTree(tree: UndoTree, key: string): void {
  try {
    const serialized: SerializedTree = {
      version: STORAGE_VERSION,
      nodes: Array.from(tree.nodes.values()).map((node) => ({
        id: node.id,
        parentId: node.parentId,
        childIds: node.childIds,
        timestamp: node.timestamp,
        operationType: node.operationType,
        operationLabel: node.operationLabel,
        delta: node.delta,
        activeChildIndex: node.activeChildIndex,
      })),
      rootId: tree.rootId,
      currentId: tree.currentId,
    };

    const json = JSON.stringify(serialized);
    localStorage.setItem(key, json);
  } catch (error) {
    console.warn("Failed to save undo tree to localStorage:", error);
  }
}

/**
 * Load the undo tree from localStorage.
 */
export function loadTree(key: string): UndoTree | null {
  try {
    const json = localStorage.getItem(key);
    if (!json) return null;

    const serialized: SerializedTree = JSON.parse(json);

    // Version check
    if (serialized.version !== STORAGE_VERSION) {
      console.warn("Undo tree version mismatch, discarding saved tree");
      localStorage.removeItem(key);
      return null;
    }

    // Validate required fields
    if (!serialized.rootId || !serialized.currentId || !serialized.nodes) {
      console.warn("Undo tree missing required fields, discarding");
      localStorage.removeItem(key);
      return null;
    }

    // Reconstruct the Map
    const nodes = new Map<string, UndoNode>();
    for (const node of serialized.nodes) {
      nodes.set(node.id, node as UndoNode);
    }

    // Validate root and current exist
    if (!nodes.has(serialized.rootId) || !nodes.has(serialized.currentId)) {
      console.warn("Undo tree root or current node not found, discarding");
      localStorage.removeItem(key);
      return null;
    }

    return {
      nodes,
      rootId: serialized.rootId,
      currentId: serialized.currentId,
    };
  } catch (error) {
    console.warn("Failed to load undo tree from localStorage:", error);
    return null;
  }
}

/**
 * Clear the saved tree.
 */
export function clearTree(key: string): void {
  localStorage.removeItem(key);
}

/**
 * Check if a saved tree exists.
 */
export function hasSavedTree(key: string): boolean {
  return localStorage.getItem(key) !== null;
}

/**
 * Get storage size estimate for the tree.
 */
export function getStorageSize(key: string): number {
  const json = localStorage.getItem(key);
  return json ? json.length * 2 : 0; // UTF-16 encoding = 2 bytes per char
}
